import { useEffect, useState } from "react";
import { MapPin, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";
import { StatusBadge } from "./PortalDashboard";
import { BookAgain, ReviewForm, CashOption } from "./BookingActions";

const GROUPS = [
  { key: "approved", title: "Upcoming" },
  { key: "pending", title: "Pending Approval" },
  { key: "past", title: "History" },
];

export default function PortalBookings() {
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);

  const load = () => {
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/reviews/mine").then((r) => setReviews(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const payDeposit = async (b) => {
    try {
      const { data } = await api.post("/payments/deposit/checkout", { booking_id: b.booking_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const grouped = {
    approved: bookings.filter((b) => b.status === "approved"),
    pending: bookings.filter((b) => b.status === "pending"),
    past: bookings.filter((b) => ["completed", "cancelled", "declined"].includes(b.status)),
  };

  return (
    <div data-testid="portal-bookings">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Your <span className="italic text-purple-700">bookings</span></h1>
        <p className="text-sm text-slate-500 mt-2">Nothing is confirmed until Empress approves it.</p>
      </FadeUp>
      {GROUPS.map((g) => (
        <div key={g.key} className="mt-10">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-400 mb-4">{g.title}</p>
          <div className="space-y-4">
            {grouped[g.key].map((b) => (
              <FadeUp key={b.booking_id} className="bg-white rounded-2xl border border-purple-100 p-6 flex flex-wrap items-center gap-4" data-testid={`booking-card-${b.booking_id}`}>
                <div className="flex-1 min-w-[220px]">
                  <p className="font-serif text-xl text-obsidian">{b.service_name}</p>
                  <p className="text-sm text-slate-500 mt-1">{b.date} at {b.time}</p>
                  <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-1"><MapPin size={13} /> {b.location_name}</p>
                  {b.admin_notes && <p className="text-xs text-slate-400 italic mt-2">Empress: {b.admin_notes}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-serif text-xl text-purple-700">${b.price}</span>
                  <StatusBadge status={b.status} />
                  {b.status === "approved" && b.deposit_status === "paid" && (
                    <span className="text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800" data-testid={`deposit-paid-${b.booking_id}`}>
                      {b.deposit_percent ?? 50}% deposit paid
                    </span>
                  )}
                  {b.status === "approved" && b.deposit_status === "cash" && (
                    <span className="text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800" data-testid={`deposit-cash-${b.booking_id}`}>
                      Cash deposit confirmed
                    </span>
                  )}
                  {b.status === "approved" && b.deposit_status === "cash_pending" && (
                    <span className="text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-100 text-amber-800" data-testid={`deposit-cash-pending-${b.booking_id}`}>
                      Cash deposit · awaiting Empress
                    </span>
                  )}
                  {b.status === "approved" && b.deposit_status === "waived" && (
                    <span className="text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid={`deposit-waived-${b.booking_id}`}>
                      No deposit required
                    </span>
                  )}
                  {b.status === "approved" && ["unpaid", "cash_pending"].includes(b.deposit_status) && b.deposit_amount > 0 && (
                    <button onClick={() => payDeposit(b)} data-testid={`pay-deposit-${b.booking_id}`}
                      className="px-5 py-2.5 rounded-full bg-gold text-obsidian text-xs font-semibold hover:brightness-110 transition-all">
                      Pay {b.deposit_percent ?? 50}% deposit · ${b.deposit_amount}
                    </button>
                  )}
                  {b.status === "completed" && <BookAgain booking={b} />}
                  {b.status === "completed" && <ReviewForm booking={b} existing={reviews.find((r) => r.booking_id === b.booking_id)} onDone={load} />}
                  {["pending", "approved"].includes(b.status) && (
                    <button onClick={() => cancel(b.booking_id)} data-testid={`booking-cancel-${b.booking_id}`}
                      className="w-9 h-9 rounded-full border border-purple-100 flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors" aria-label="Cancel booking">
                      <X size={15} />
                    </button>
                  )}
                </div>
                {b.status === "approved" && b.deposit_status === "unpaid" && b.deposit_amount > 0 && (
                  <div className="w-full flex justify-end"><CashOption booking={b} onDone={load} /></div>
                )}
              </FadeUp>
            ))}
            {grouped[g.key].length === 0 && <p className="text-sm text-slate-400 italic">No {g.title.toLowerCase()} bookings.</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
